import { useQuery } from "@apollo/client/react";
import { useAtomValue } from "jotai";
import { useMemo } from "react";

import type { UpcomingEventsQuery } from "@/graphql/__generated__/graphql";
import type { EventRow } from "@/utils/schemas";

import { currentUserIdAtom } from "@/atoms/session";
import {
  PAST_USER_EVENTS_QUERY,
  UPCOMING_EVENTS_QUERY,
} from "@/graphql/events-list";
import { type DisplayEvent, toDisplayEvent } from "@/utils/event-helpers";

type EventNode = NonNullable<
  NonNullable<UpcomingEventsQuery["eventCollection"]>["edges"][number]["node"]
>;

export function useEventList(): {
  loading: boolean;
  past: DisplayEvent[];
  refetch: () => void;
  upcoming: DisplayEvent[];
} {
  const userId = useAtomValue(currentUserIdAtom);
  const now = useMemo(() => new Date().toISOString(), []);

  const {
    data: upcomingData,
    loading: upcomingLoading,
    refetch: refetchUpcoming,
  } = useQuery(UPCOMING_EVENTS_QUERY, {
    variables: { now },
  });

  const {
    data: pastData,
    loading: pastLoading,
    refetch: refetchPast,
  } = useQuery(PAST_USER_EVENTS_QUERY, {
    skip: !userId,
    variables: { now },
  });

  const upcoming = useMemo(() => {
    const edges = upcomingData?.eventCollection?.edges ?? [];
    return edges
      .filter((edge) => {
        if (edge.node.visibility !== "invite_only") return true;
        const members = edge.node.event_memberCollection?.edges ?? [];
        return members.some((m) => m.node.user_id === userId);
      })
      .map((edge) => toDisplayEvent(toEventRow(edge.node), userId));
  }, [upcomingData, userId]);

  const past = useMemo(() => {
    const edges = pastData?.eventCollection?.edges ?? [];
    return edges
      .filter((edge) => {
        const members = edge.node.event_memberCollection?.edges ?? [];
        return members.some((m) => m.node.user_id === userId);
      })
      .map((edge) => toDisplayEvent(toEventRow(edge.node), userId));
  }, [pastData, userId]);

  const refetch = () => {
    refetchUpcoming();
    if (userId) refetchPast();
  };

  return { loading: upcomingLoading || pastLoading, past, refetch, upcoming };
}

function toEventRow(node: EventNode): EventRow {
  return {
    description: node.description ?? null,
    ends_at: node.ends_at ?? null,
    event_member:
      node.event_memberCollection?.edges.map((e) => ({
        is_contact: false,
        name: null,
        role: e.node.role ?? "",
        rsvp_status: e.node.rsvp_status ?? "",
        user_id: e.node.user_id ?? "",
      })) ?? [],
    event_ticket_kind:
      node.event_ticket_kindCollection?.edges.map((e) => ({
        currency: e.node.currency ?? "",
        description: e.node.description ?? null,
        id: e.node.id ?? "",
        price_cents: e.node.price_cents ?? 0,
        price_service_fee: e.node.price_service_fee ?? null,
        quantity_sold: e.node.quantity_sold ?? null,
        quantity_total: e.node.quantity_total ?? null,
      })) ?? [],
    flyer_url: node.flyer_url ?? null,
    id: node.id ?? "",
    location_city: node.location_city ?? null,
    location_geo: node.location_geo ?? null,
    location_name: node.location_name ?? null,
    location_neighborhood: node.location_neighborhood ?? null,
    location_place_id: node.location_place_id ?? null,
    location_postal_code: node.location_postal_code ?? null,
    location_state: node.location_state ?? null,
    location_street: node.location_street ?? null,
    slug: node.slug ?? null,
    starts_at: node.starts_at ?? null,
    status: node.status ?? "",
    title: node.title ?? null,
    visibility: node.visibility ?? "",
  };
}
